import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Award } from 'lucide-react';

const StrategicConsulting = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [name, setName] = useState('');

  const handleRequestDiagnostic = () => {
    if (!email || !company || !name) {
      alert('Please fill in all required fields');
      return;
    }
    
    const subject = 'Strategic Consulting Request - Comprehensive Business Diagnostics';
    const body = `Hello Jan,

I would like to request a comprehensive business diagnostic consultation.

COMPANY INFORMATION:
Name: ${name}
Company: ${company}
Email: ${email}

DIAGNOSTIC INTEREST:
I need a strategic analysis that goes beyond traditional SWOT analysis, including:

• Current State Assessment (financial health, operational performance, process maturity)
• Competitive Positioning (market performance, benchmarking, gaps analysis)
• Strategic Opportunity Mapping (improvement potential, QRM applications, investment priorities)
• Implementation Roadmap (phased approach with timelines and ROI projections)

Please advise on:
• Comprehensive diagnostic process
• Expected timeline and deliverables
• Investment required for strategic consulting
• Next steps for engagement

Looking forward to discussing how your strategic consulting can help transform our business.

Best regards,
${name}
${company}`;
    
    window.open(`mailto:info@smartqrmnavigator.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800">
      <PageSEO path="/strategic-consulting" />
      <div className="container mx-auto px-6 py-24">
        <div className="max-w-4xl mx-auto text-center">
          
          {/* Main Headline */}
          <div className="mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-8 leading-tight">
              QRM Strategic Consulting &
              <span className="text-yellow-300"> Business Diagnostics</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 leading-relaxed max-w-4xl mx-auto">
              Go beyond traditional SWOT analysis with comprehensive business diagnostics 
              that reveal hidden opportunities and competitive advantages.
            </p>
          </div>

          {/* Comprehensive Business Diagnostics Framework */}
          <Card className="p-8 bg-white/10 backdrop-blur-sm border-white/20 mb-12">
            <h2 className="text-2xl font-bold text-white mb-8">Comprehensive Business Diagnostics Framework</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Current State Assessment */}
              <div className="bg-white/10 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-yellow-300 mb-4">1. Current State Assessment</h3>
                <ul className="text-blue-100 text-sm space-y-2 text-left">
                  <li>• <strong>Financial Health:</strong> Revenue, EBITDA, cash conversion, working capital</li>
                  <li>• <strong>Operational Performance:</strong> Lead times, defect rates, throughput</li>
                  <li>• <strong>Process Maturity:</strong> MCT analysis, WIP levels, bottlenecks</li>
                  <li>• <strong>Organizational Readiness:</strong> Team capabilities, change readiness</li>
                </ul>
              </div>

              {/* Competitive Positioning */}
              <div className="bg-white/10 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-yellow-300 mb-4">2. Competitive Positioning</h3>
                <ul className="text-blue-100 text-sm space-y-2 text-left">
                  <li>• <strong>Market Performance:</strong> Ranking vs 3 competitors</li>
                  <li>• <strong>Financial Benchmarking:</strong> EBITDA, cash flow vs industry</li>
                  <li>• <strong>Operational Gaps:</strong> Where competitors outperform</li>
                  <li>• <strong>Competitive Advantages:</strong> Where you hold the edge</li>
                </ul>
              </div>

              {/* Strategic Opportunity Mapping */}
              <div className="bg-white/10 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-yellow-300 mb-4">3. Strategic Opportunity Mapping</h3>
                <ul className="text-blue-100 text-sm space-y-2 text-left">
                  <li>• <strong>Improvement Potential:</strong> Quantified financial opportunities</li>
                  <li>• <strong>QRM Applications:</strong> Fit assessment vs Lean/TOC</li>
                  <li>• <strong>Investment Priorities:</strong> Training, capacity, technology</li>
                  <li>• <strong>Quick Wins:</strong> Immediate high-ROI actions</li>
                </ul>
              </div>

              {/* Implementation Roadmap */}
              <div className="bg-white/10 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-yellow-300 mb-4">4. Implementation Roadmap</h3>
                <ul className="text-blue-100 text-sm space-y-2 text-left">
                  <li>• <strong>Phase 1:</strong> Immediate actions (3-6 months)</li>
                  <li>• <strong>Phase 2:</strong> Mid-term transformation (6-18 months)</li>
                  <li>• <strong>Phase 3:</strong> Long-term optimization (18+ months)</li>
                  <li>• <strong>ROI Tracking:</strong> Expected returns per phase</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Contact Form */}
          <Card className="p-8 bg-white/95 backdrop-blur-sm shadow-2xl max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Request Strategic Consulting</h2>
            
            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium mb-2 block text-left">Full Name *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full text-lg"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block text-left">Company Name *</Label>
                <Input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Your Manufacturing Company"
                  className="w-full text-lg"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block text-left">Email Address *</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john.smith@company.com"
                  className="w-full text-lg"
                />
              </div>
              
              <Button 
                size="lg"
                className="w-full py-4 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xl shadow-xl"
                onClick={handleRequestDiagnostic}
              >
                <Award className="mr-3 h-6 w-6" />
                Request Strategic Consulting
              </Button>
            </div>
          </Card>

          {/* Navigation */}
          <div className="text-center">
            <Button 
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-blue-600"
              onClick={() => navigate('/')}
            >
              ← Back to Home
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default StrategicConsulting;
